import { logger } from '@/utils/logger';
import { useEffect } from 'react';
import { refreshAccessToken } from '@/lib/api-client';
import {
    endSession,
    getAccessTokenSecondsRemaining,
    isSessionEnding,
} from '@/lib/session';

const CHECK_INTERVAL_MS = 30_000;
const REFRESH_MARGIN_SECONDS = 300;

/**
 * Keeps the access token alive while the user is signed in, and ends the session
 * when it can no longer be renewed. Inactivity is handled by useIdleTimeout.
 */
export const useAuthGuard = () => {
    useEffect(() => {
        let cancelled = false;

        const checkAccessToken = async () => {
            if (cancelled || isSessionEnding()) {
                return;
            }
            if (!localStorage.getItem('auth_token')) {
                return;
            }

            const secondsRemaining = getAccessTokenSecondsRemaining();
            if (secondsRemaining === null) {
                logger.warn('Access token is unreadable - ending session');
                endSession('expired');
                return;
            }

            if (secondsRemaining > REFRESH_MARGIN_SECONDS) {
                return;
            }

            const refreshed = await refreshAccessToken();
            if (cancelled || refreshed) {
                return;
            }

            const remainingAfterAttempt = getAccessTokenSecondsRemaining();
            if (remainingAfterAttempt === null || remainingAfterAttempt <= 0) {
                logger.warn('Access token expired and could not be refreshed - ending session');
                endSession('expired');
            }
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkAccessToken();
            }
        };

        checkAccessToken();
        const interval = setInterval(checkAccessToken, CHECK_INTERVAL_MS);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            cancelled = true;
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);
};
